import * as THREE from "three";
import { seaSurface } from '@hyperlight/shared';
import type { Planet } from "../world/planet";
import { WEATHER, WEATHER_MAP_GLSL, weatherTime } from "../world/weather/weatherField";
import { WEATHER_SURFACE_GLSL, type WeatherSurface } from "../world/weather/weatherSurface";
import { CLOUD_MAPPING_GLSL } from "../world/weather/cloudMapping";
import { TerrainMistField, TERRAIN_MIST_GLSL } from "../world/weather/terrainMist";
import { VOLCANIC_PLUME_GLSL } from '../world/volcano/plumeShader';
import {DistantSceneDepth,DISTANT_DEPTH_GLSL} from './distantSceneDepth';

export interface AtmosphericLight {
  direction: THREE.Vector3;
  /** Real celestial radiance; excludes gameplay night/starlight floors. */
  color: THREE.Color;
  shadow?: THREE.DirectionalLight;
}
export interface AtmosphereFrame {
  planet: Planet;
  lights: readonly AtmosphericLight[];
  surface: WeatherSurface;
  ambient: THREE.Color;
  presence: number;
  mist: number;
}
const VS = `varying vec2 vUv; void main(){vUv=uv;gl_Position=vec4(position.xy,0.0,1.0);}`;
const DEPTH = /* glsl */ `
uniform vec2 uDepthRange;
float viewDepth(float d) {
#ifdef LOG_DEPTH
  return exp2(d * log2(uDepthRange.y + 1.0)) - 1.0;
#else
  return uDepthRange.x * uDepthRange.y / (uDepthRange.y - d * (uDepthRange.y - uDepthRange.x));
#endif
}
`;
const MARCH = /* glsl */ `
#include <packing>
varying vec2 vUv;
uniform sampler2D uDepth, uShadow0, uShadow1, uCloud;
uniform mat4 uShadowMatrix0, uShadowMatrix1, uCameraLocal;
uniform mat4 uPlanetMatrix;
uniform mat3 uCloudFrame;
uniform vec2 uTan;
uniform vec3 uLightDir[3], uLightColor[3], uAmbient;
uniform vec2 uShadowOn, uShadowSize0, uShadowSize1;
uniform float uRange, uDensity, uCloudR, uSeaR, uTime;
${DEPTH}
${DISTANT_DEPTH_GLSL}
${WEATHER_MAP_GLSL}
${WEATHER_SURFACE_GLSL}
${TERRAIN_MIST_GLSL}
${CLOUD_MAPPING_GLSL}
${VOLCANIC_PLUME_GLSL}

float noise(vec3 p) {
  // Low-frequency planet-anchored density: no camera-following fog plane.
  return 0.75 + 0.25 * sin(dot(p, vec3(0.019,0.013,0.027)) + uTime) * sin(dot(p,vec3(-0.024,0.018,0.011)) - uTime * 0.7);
}
float shadowAt(sampler2D tex, mat4 matrix, vec2 size, vec3 p, float on) {
  if(on < 0.001) return 1.0;
  vec4 sc = matrix * uPlanetMatrix * vec4(p,1.0);
  vec3 q = sc.xyz / sc.w;
  float edge = min(min(q.x,q.y),min(1.0-q.x,1.0-q.y));
  if(edge<=0.0 || q.z<=0.0 || q.z>=1.0) return 1.0;
  // Filter comparisons, not packed depth. A single binary texel made shafts
  // switch brightness as the player-centred shadow map moved across geometry.
  vec2 grid=q.xy*size-0.5, base=floor(grid), f=fract(grid);
  float lit=0.0;
  for(int y=0;y<=1;y++) for(int x=0;x<=1;x++) {
    vec2 uv=(base+vec2(float(x),float(y))+0.5)/size;
    float weight=(x==0?1.0-f.x:f.x)*(y==0?1.0-f.y:f.y);
    lit+=weight*step(q.z-0.00006,unpackRGBAToDepth(texture2D(tex,uv)));
  }
  return mix(1.0,lit,on*smoothstep(0.0,0.08,edge));
}
float cloudTransmission(vec3 p, vec3 lightDir) {
  float b = dot(p,lightDir);
  float disc = b*b-dot(p,p)+uCloudR*uCloudR;
  if(disc<0.0 || dot(normalize(p),lightDir)<-0.03) return 0.0;
  vec3 d = normalize(p + lightDir * (-b+sqrt(disc)));
  vec4 w = weatherMap(d);
  vec2 uv = cloudUv(uCloudFrame * d);
  // Direct forward scattering must lose the light blocked by opaque clouds.
  // Surface shadow tuning deliberately stays softer; applying that 22% mask
  // here left a bright solar/lunar halo after its disk had disappeared.
  return exp(-w.b*5.0*0.35) * (1.0-texture2D(uCloud,uv).a*0.98);
}
void main() {
  float dep=texture2D(uDepth,vUv).r;
  vec3 ray=normalize(vec3((vUv*2.0-1.0)*uTan,-1.0));
  vec3 ro=uCameraLocal[3].xyz, rd=normalize(mat3(uCameraLocal)*ray);
  float end=min(uRange,viewDepth(dep)/max(0.001,-ray.z));
  // The near ocean is transparent and does not write depth. Stop at its
  // analytic surface; rivers already supply surface depth through their ribbon.
  if(uSeaR>0.0) {
    float b=dot(ro,rd), disc=b*b-dot(ro,ro)+uSeaR*uSeaR;
    if(disc>0.0) { float hit=-b-sqrt(disc); if(hit>0.0) end=min(end,hit); }
  }
  float T=1.0;
  vec3 L=vec3(0.0);
  // Plumes remain visible above/in the cloud layer where general mist is zero.
  // Skip the unrelated weather/shadow march entirely in those views.
  if(uDensity>0.0) {
  vec3 mid=ro+rd*end*0.5;
  vec3 source[3];
  for(int j=0;j<3;j++) {
    float mu=dot(rd,uLightDir[j]);
    float g=0.62;
    float phase=0.045+0.12*(1.0-g*g)/pow(max(0.05,1.0+g*g-2.0*g*mu),1.5);
    source[j]=uLightColor[j] * min(3.0,phase);
    if(dot(source[j],source[j])>0.000001) source[j]*=cloudTransmission(mid,uLightDir[j]);
  }
  // Spatial dithering breaks up marching bands without changing the sample
  // phase every frame. At 16 steps, temporal jitter jumped whole shadow/roof
  // boundaries and kept flashing even after history had converged.
  float jitter=fract(52.9829189*fract(dot(gl_FragCoord.xy,vec2(0.06711056,0.00583715))));
  for(int i=0;i<STEPS;i++) {
    // Quadratic spacing preserves short segments around nearby roofs and
    // silhouettes while reaching the middle distance with the same step count.
    float a=float(i)/float(STEPS), b=float(i+1)/float(STEPS);
    float start=end*a*a, dt=end*(b*b-a*a);
    float distance=start+jitter*dt;
    vec3 p=ro+rd*distance;
    float h=length(p)-uWxRadius;
    vec2 uv=wxSurfaceUv(p);
    float inside=wxSurfaceInside(uv);
    vec4 s=texture2D(uWxSurface,uv);
    float edge=smoothstep(0.0,0.1,min(min(uv.x,uv.y),min(1.0-uv.x,1.0-uv.y))) * inside;
    vec3 regional=mistTerrain(p);
    // Fine nearby terrain supports roofs/routes; coarse regional terrain
    // continues through wet hollows. Unknown support creates no low layer.
    float known=step(-100000.0,s.g);
    float terrain=mix(regional.x,max(s.g,uSeaR-uWxRadius),edge*known);
    float pool=regional.y*regional.z;
    float low=exp(-max(0.0,h-terrain)/mix(9.0,26.0,pool));
    float above=smoothstep(-2.0,1.0,h-terrain);
    // Restore S07's broad, soft scattering around the observer. Keep its
    // original nearby terrain/datum and ~220 m optical reach; extending that
    // blanket across the full regional march would wash out the landscape.
    float generalTerrain=mix(max(0.0,uSeaR-uWxRadius),max(s.g,uSeaR-uWxRadius),edge*known);
    float broad=exp(-max(0.0,h-generalTerrain)/42.0);
    float general=(0.12+0.88*broad)*(1.0-smoothstep(180.0,260.0,distance));
    // Terrain-aware pooling adds a restrained second layer farther out. It
    // no longer replaces the general atmosphere with clear air on dry ridges.
    float pooled=0.20*pool*low*above*smoothstep(45.0,150.0,distance);
    float roof=inside*smoothstep(0.8,1.8,s.r-s.g)*(1.0-smoothstep(s.r-0.5,s.r+0.3,h));
    // Unknown cache cells must not be interpreted as an enormous building.
    if(s.r>100000.0) roof=0.0;
    float density=uDensity*(general+pooled)*noise(p)*(1.0-roof);
    float stepT=exp(-density*dt);
    vec3 light=uAmbient;
    if(dot(source[0],source[0])>0.000001) light+=source[0]*shadowAt(uShadow0,uShadowMatrix0,uShadowSize0,p,uShadowOn.x);
    if(dot(source[1],source[1])>0.000001) light+=source[1]*shadowAt(uShadow1,uShadowMatrix1,uShadowSize1,p,uShadowOn.y);
    light+=source[2]; // secondary moon: broad, explicitly unshadowed contribution
    L+=T*(1.0-stepT)*light;
    T*=stepT;
  }
  }
  vec4 vent=volcanicPlumes(ro,rd,completeViewDepth(vUv,viewDepth(dep))/max(0.001,-ray.z));
  gl_FragColor=vec4(L+T*vent.rgb,T*vent.a);
  gl_FragDepth=dep;
}
`;
const HISTORY = /* glsl */ `
varying vec2 vUv;
uniform sampler2D uCurrent, uCurrentDepth, uHistory, uHistoryDepth;
uniform mat4 uCameraLocal, uPreviousClip;
uniform vec2 uTan, uTexel;
uniform float uHistoryWeight, uRange;
${DEPTH}
void main() {
  float d=texture2D(uCurrentDepth,vUv).r;
  vec3 ray=vec3((vUv*2.0-1.0)*uTan,-1.0);
  vec3 local=(uCameraLocal*vec4(ray*(d>0.999999?uRange:viewDepth(d)),1.0)).xyz;
  vec4 clip=uPreviousClip*vec4(local,1.0);
  vec2 uv=clip.xy/clip.w*0.5+0.5;
  float valid=step(0.0,min(uv.x,uv.y))*step(max(uv.x,uv.y),1.0)*step(0.001,clip.w);
  float expected=clip.w;
  vec4 cur=texture2D(uCurrent,vUv), lo=cur, hi=cur;
  for(int y=-1;y<=1;y++) for(int x=-1;x<=1;x++) {
    vec4 n=texture2D(uCurrent,vUv+vec2(float(x),float(y))*uTexel);
    lo=min(lo,n);hi=max(hi,n);
  }
  // Reprojection generally lands BETWEEN pixels. Nearest sampling snapped the
  // low-resolution fog between old texels during walking. Gather bilinearly,
  // rejecting each tap independently so foreground fog cannot leak into sky.
  vec2 grid=uv/uTexel-0.5, base=floor(grid), f=fract(grid);
  vec4 sum=vec4(0.0); float total=0.0;
  for(int y=0;y<=1;y++) for(int x=0;x<=1;x++) {
    vec2 at=(base+vec2(float(x),float(y))+0.5)*uTexel;
    float old=texture2D(uHistoryDepth,at).r;
    float error=abs(viewDepth(old)-expected);
    float match=d>0.999999?step(0.999999,old):1.0-smoothstep(0.6+expected*0.008,1.8+expected*0.015,error);
    float weight=(x==0?1.0-f.x:f.x)*(y==0?1.0-f.y:f.y)*match;
    weight*=step(0.0,min(at.x,at.y))*step(max(at.x,at.y),1.0);
    sum+=texture2D(uHistory,at)*weight; total+=weight;
  }
  vec4 previous=clamp(sum/max(total,0.00001),lo,hi);
  gl_FragColor=mix(cur,previous,uHistoryWeight*valid*min(1.0,total));
  gl_FragDepth=d;
}
`;

const white = new THREE.DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1);
white.needsUpdate = true;

/** Two quarter-dimension passes, with no extra scene/shadow render. */
export class VolumetricAtmosphere {
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private readonly quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2));
  private targets: THREE.WebGLRenderTarget[] = [];
  private march: THREE.ShaderMaterial | null = null;
  private history: THREE.ShaderMaterial | null = null;
  private readonly localCamera = new THREE.Matrix4();
  private readonly previousClip = new THREE.Matrix4();
  private readonly projection = new THREE.PerspectiveCamera();
  private readonly lastPosition = new THREE.Vector3();
  private readonly lastDirection = new THREE.Vector3();
  private readonly lastMoonDirection = new THREE.Vector3();
  private readonly position = new THREE.Vector3();
  private readonly direction = new THREE.Vector3();
  private readonly invQuat = new THREE.Quaternion();
  private lastPlanet: Planet | null = null;
  private lastEnergy = 0;
  private frame = 0;
  private valid = false;
  private logDepth = false;
  private quality = "";
  private readonly lightDir = Array.from({ length: 3 }, () => new THREE.Vector3());
  private readonly lightColor = Array.from({ length: 3 }, () => new THREE.Color(0, 0, 0));
  private readonly mistField = new TerrainMistField();
  readonly distantDepth=new DistantSceneDepth();

  constructor() { this.quad.frustumCulled = false; this.scene.add(this.quad); }
  reset(): void { this.valid = false; }
  private build(width: number, height: number, logDepth: boolean): void {
    this.disposeTargets();
    this.logDepth = logDepth; this.quality = WEATHER.quality;
    const scale = WEATHER.quality === "high" ? 2 : 4;
    const w = Math.max(2, Math.ceil(width / scale)), h = Math.max(2, Math.ceil(height / scale));
    for (let i = 0; i < 3; i++) {
      const depth = new THREE.DepthTexture(w, h, THREE.UnsignedIntType);
      const target = new THREE.WebGLRenderTarget(w, h, { type: THREE.HalfFloatType, depthBuffer: true, depthTexture: depth });
      target.texture.minFilter = target.texture.magFilter = THREE.NearestFilter;
      this.targets.push(target);
    }
    const shared = {
      uDepthRange: { value: new THREE.Vector2() }, uTan: { value: new THREE.Vector2() },
      uCameraLocal: { value: this.localCamera }, uRange: { value: 640 },
    };
    this.march = new THREE.ShaderMaterial({ vertexShader: VS, fragmentShader: MARCH,
      defines: { STEPS: WEATHER.quality === "high" ? 24 : 16, VENT_STEPS: WEATHER.quality === "high" ? 48 : 24, ...(logDepth ? { LOG_DEPTH: 1 } : {}) },
      depthTest: true, depthFunc: THREE.AlwaysDepth, depthWrite: true,
      uniforms: { ...shared,...this.distantDepth.uniforms, uDepth: { value: null }, uCloud: { value: white },
        uShadow0: { value: white }, uShadow1: { value: white }, uShadowMatrix0: { value: new THREE.Matrix4() }, uShadowMatrix1: { value: new THREE.Matrix4() },
        uShadowOn: { value: new THREE.Vector2() }, uShadowSize0: { value: new THREE.Vector2(1, 1) },
        uShadowSize1: { value: new THREE.Vector2(1, 1) }, uPlanetMatrix: { value: new THREE.Matrix4() },
        uLightDir: { value: this.lightDir }, uLightColor: { value: this.lightColor }, uAmbient: { value: new THREE.Color() },
        uDensity: { value: 0 }, uCloudR: { value: 0 }, uCloudFrame: { value: new THREE.Matrix3() }, uSeaR: { value: 0 }, uTime: { value: 0 },
        uVentCount:{value:0},uVentNoiseTime:{value:0},
        ...Object.fromEntries(['uVentOrigin','uVentUp','uVentRight','uVentForward'].map(k=>[k,{value:Array.from({length:3},()=>new THREE.Vector3())}])),
        ...Object.fromEntries(['uVentShape','uVentWindPhase'].map(k=>[k,{value:Array.from({length:3},()=>new THREE.Vector4())}])),
      } });
    this.history = new THREE.ShaderMaterial({ vertexShader: VS, fragmentShader: HISTORY,
      defines: logDepth ? { LOG_DEPTH: 1 } : {}, depthTest: true, depthFunc: THREE.AlwaysDepth, depthWrite: true,
      uniforms: { ...shared, uCurrent: { value: this.targets[0].texture }, uCurrentDepth: { value: this.targets[0].depthTexture },
        uHistory: { value: null }, uHistoryDepth: { value: null }, uPreviousClip: { value: this.previousClip },
        uHistoryWeight: { value: 0 }, uTexel: { value: new THREE.Vector2(1 / w, 1 / h) } } });
  }
  render(renderer: THREE.WebGLRenderer, camera: THREE.PerspectiveCamera, depth: THREE.Texture,
    width: number, height: number, near: number, far: number, frame: AtmosphereFrame): THREE.WebGLRenderTarget | null {
    const hasVents=!!frame.planet.volcanicEffects?.group.visible && frame.planet.volcanicEffects.plumeUniforms.uVentCount.value>0;
    if ((!hasVents && (!WEATHER.volumetrics || WEATHER.strength <= 0 || frame.presence < 0.001)) || !frame.planet.def.atmosphere || !frame.planet.weatherField ||
      (seaSurface(frame.planet.def) === 'water' && frame.planet.def.seaRadius !== null && camera.position.distanceTo(frame.planet.group.position) < frame.planet.def.seaRadius)) { this.reset(); return null; }
    if (!renderer.extensions.has("EXT_color_buffer_float")) return null;
    const scale = WEATHER.quality === "high" ? 2 : 4;
    if (!this.targets.length || this.targets[0].width !== Math.max(2, Math.ceil(width / scale)) || this.targets[0].height !== Math.max(2, Math.ceil(height / scale)) || this.quality !== WEATHER.quality || this.logDepth !== renderer.capabilities.logarithmicDepthBuffer)
      this.build(width, height, renderer.capabilities.logarithmicDepthBuffer);
    const p = frame.planet, u = this.march!.uniforms;
    p.group.updateWorldMatrix(true, false);
    this.localCamera.copy(p.group.matrixWorld).invert().multiply(camera.matrixWorld);
    this.mistField.update(p,this.position.setFromMatrixPosition(this.localCamera),weatherTime());
    Object.assign(u, p.weatherField!.uniforms, frame.surface.uniforms,this.mistField.uniforms);
    if(p.volcanicEffects?.group.visible)Object.assign(u,p.volcanicEffects.plumeUniforms);
    else u.uVentCount={value:0};
    u.uPlanetMatrix.value.copy(p.group.matrixWorld);
    u.uDepth.value = depth;
    if (u.uDepthRange.value.x !== near || u.uDepthRange.value.y !== far) this.reset();
    u.uDepthRange.value.set(near, far);
    const tan = Math.tan(THREE.MathUtils.degToRad(camera.fov * 0.5)) / camera.zoom;
    u.uTan.value.set(tan * camera.aspect, tan);
    u.uRange.value = Math.min(640, far * 0.85);
    const cloud = p.getCloudShadow();
    u.uCloud.value = cloud?.tex ?? white;
    u.uCloudR.value = cloud?.radius ?? p.def.radius;
    if (cloud) u.uCloudFrame.value.copy(cloud.frame); else u.uCloudFrame.value.identity();
    u.uSeaR.value = p.def.seaRadius ?? 0;
    u.uDensity.value = (WEATHER.volumetrics?WEATHER.strength:0) * frame.presence * (0.00008 + frame.mist * 0.0022);
    u.uAmbient.value.copy(frame.ambient).multiplyScalar(0.32);
    u.uTime.value = (weatherTime() / 80) % (Math.PI * 20);
    this.frame++;
    const inv = this.invQuat.copy(p.group.quaternion).invert();
    let energy = 0;
    for (let i = 0; i < 3; i++) {
      const l = frame.lights[i];
      this.lightDir[i].copy(l?.direction ?? camera.up).applyQuaternion(inv);
      if (l) this.lightColor[i].copy(l.color); else this.lightColor[i].setRGB(0, 0, 0);
      energy += this.lightColor[i].r + this.lightColor[i].g + this.lightColor[i].b;
      if (i < 2) {
        const s = l?.shadow?.shadow;
        u[`uShadow${i}`].value = s?.map?.texture ?? white;
        u[`uShadowSize${i}`].value.set(s?.map?.width ?? 1, s?.map?.height ?? 1);
        if (s) u[`uShadowMatrix${i}`].value.copy(s.matrix);
        u.uShadowOn.value.setComponent(i, l?.shadow?.castShadow && s?.map ? s.intensity : 0);
      }
    }
    const position = this.position.setFromMatrixPosition(this.localCamera);
    const direction = this.direction.set(0, 0, -1).transformDirection(this.localCamera);
    if (p !== this.lastPlanet || position.distanceToSquared(this.lastPosition) > 10000 || direction.dot(this.lastDirection) < 0.8 || Math.abs(energy - this.lastEnergy) > 0.15 || this.lightDir[1].dot(this.lastMoonDirection) < 0.97) this.reset();
    const prevTarget = renderer.getRenderTarget();
    const auto = renderer.autoClear;
    renderer.autoClear = true;
    this.quad.material = this.march!; renderer.setRenderTarget(this.targets[0]); renderer.render(this.scene, this.camera);
    const index = 1 + this.frame % 2, old = 1 + (this.frame + 1) % 2;
    const hu = this.history!.uniforms;
    hu.uHistory.value = this.targets[old].texture; hu.uHistoryDepth.value = this.targets[old].depthTexture;
    // Local moving plumes can lie kilometres behind the generic mist's 640 m
    // reprojection plane. Do not drag that old plane across their silhouettes.
    hu.uHistoryWeight.value = this.valid ? (u.uVentCount.value>0?.18:.82) : 0;
    this.quad.material = this.history!; renderer.setRenderTarget(this.targets[index]); renderer.render(this.scene, this.camera);
    renderer.setRenderTarget(prevTarget); renderer.autoClear = auto;
    this.projection.copy(camera); this.projection.near = near; this.projection.far = far; this.projection.updateProjectionMatrix();
    this.previousClip.copy(this.projection.projectionMatrix).multiply(camera.matrixWorldInverse).multiply(p.group.matrixWorld);
    this.lastPosition.copy(position); this.lastDirection.copy(direction); this.lastPlanet = p; this.lastEnergy = energy; this.valid = true;
    this.lastMoonDirection.copy(this.lightDir[1]);
    return this.targets[index];
  }
  private disposeTargets(): void {
    for (const t of this.targets) { t.depthTexture?.dispose(); t.dispose(); }
    this.targets = []; this.march?.dispose(); this.history?.dispose(); this.valid = false;
  }
  dispose(): void { this.disposeTargets(); this.quad.geometry.dispose(); this.mistField.dispose();this.distantDepth.dispose(); }
}

/** Four depth-aware taps at full resolution, composed in the post's working
 * space BEFORE grading. Invalid edge taps fall back to the closest depth. */
export const VOLUME_COMPOSITE_GLSL = /* glsl */ `
${DISTANT_DEPTH_GLSL}
uniform sampler2D uVolume, uVolumeDepth;
uniform vec2 uVolumeSize;
uniform float uVolumeOn;
vec3 applyAtmosphere(vec3 scene, vec2 uv, float depth) {
  if(uVolumeOn<0.5) return scene;
  depth=completeViewDepth(uv,depth);
  vec2 grid=uv*uVolumeSize-0.5, base=floor(grid), f=fract(grid);
  vec4 sum=vec4(0.0), best=vec4(0.0,0.0,0.0,1.0);
  float total=0.0, errorBest=1e20, bestDepth=1.0;
  for(int y=0;y<=1;y++) for(int x=0;x<=1;x++) {
    vec2 at=(base+vec2(float(x),float(y))+0.5)/uVolumeSize;
    float z=completeViewDepth(at,linearDepth(texture2D(uVolumeDepth,at).r));
    float error=abs(depth-z);
    vec4 v=texture2D(uVolume,at);
    if(error<errorBest) {errorBest=error;best=v;bestDepth=z;}
    float w=(x==0?1.0-f.x:f.x)*(y==0?1.0-f.y:f.y)*exp(-error/max(0.4,depth*0.01));
    sum+=v*w;total+=w;
  }
  vec4 fog=total>0.00001?sum/total:best;
  if(total<=0.00001 && bestDepth>depth) {
    // Thin foreground geometry may miss every coarse tap. Shorten the optical
    // segment instead of painting the background's fog across its silhouette.
    float T=pow(max(0.00001,fog.a),clamp(depth/bestDepth,0.0,1.0));
    fog.rgb*=(1.0-T)/max(0.00001,1.0-fog.a);
    fog.a=T;
  }
  // Gamma-2 working space is intentionally used by PainterlyPost. Convert the
  // integrated linear radiance to that same space without decoding scene twice.
  vec3 radiance=fog.rgb;
  #ifdef SRGB_SCENE
    vec3 encoded=toSRGB(max(radiance,vec3(0.0)));
    radiance=encoded*encoded;
  #endif
  return scene*fog.a+radiance;
}
`;
